/**
 * Status badge component for displaying inquiry status
 * Shows color-coded status with appropriate icons
 */

import { Badge } from '@/components/ui/badge';
import { InquiryStatus } from '@/modules/marketplace/types/inquiries.types';
import { AlertCircle, CheckCircle, Clock, X } from 'lucide-react';

interface InquiryStatusBadgeProps {
  status: InquiryStatus;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
}

const STATUS_CONFIG = {
  [InquiryStatus.NEW]: {
    label: 'New',
    variant: 'default' as const,
    color: 'bg-blue-100 text-blue-800',
    icon: AlertCircle,
  },
  [InquiryStatus.CONTACTED]: {
    label: 'Contacted',
    variant: 'secondary' as const,
    color: 'bg-yellow-100 text-yellow-800',
    icon: Clock,
  },
  [InquiryStatus.CONVERTED]: {
    label: 'Converted',
    variant: 'default' as const,
    color: 'bg-green-100 text-green-800',
    icon: CheckCircle,
  },
  [InquiryStatus.CLOSED]: {
    label: 'Closed',
    variant: 'secondary' as const,
    color: 'bg-gray-100 text-gray-800',
    icon: X,
  },
};

export function InquiryStatusBadge({
  status,
  size = 'md',
  showIcon = true,
}: InquiryStatusBadgeProps) {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };

  return (
    <div className={`inline-flex items-center gap-1 ${config.color} rounded-full ${sizeClasses[size]}`}>
      {showIcon && <Icon className={size === 'sm' ? 'h-3 w-3' : size === 'md' ? 'h-4 w-4' : 'h-5 w-5'} />}
      <span className="font-medium">{config.label}</span>
    </div>
  );
}

/**
 * Get color class for status
 */
export function getStatusColor(status: InquiryStatus): string {
  return STATUS_CONFIG[status].color;
}

/**
 * Get status label
 */
export function getStatusLabel(status: InquiryStatus): string {
  return STATUS_CONFIG[status].label;
}
